import { api } from '@/lib/trpc/react';
import { toast } from 'sonner';

interface MoveToFolderInput {
  ids: string[];
  folderId: string | null;
  teamId?: string | null;
}

/**
 * Hook to move one or more workspace items (chatbots) into a folder (chatbot of type `GROUP`).
 * It performs an optimistic update on `workspace.all` cache so that the UI instantly reflects
 * the new `groupId` and `teamId` for moved items.
 */
export const useMoveToFolder = () => {
  const utils = api.useUtils();

  const mutation = api.chatbot.moveToFolder.useMutation({
    // Optimistic update
    onMutate: async ({ ids, folderId, teamId }: MoveToFolderInput) => {
      // Snapshot previous value
      const prevData = utils.workspace.all.getData();

      // Show toast notification immediately
      const itemCount = ids.length;

      if (folderId) {
        // Get the folder name from workspace data
        const workspaceData = utils.workspace.all.getData();
        const targetFolder = workspaceData?.entities.find(
          (entity) => entity.id === folderId,
        );
        const folderName = targetFolder?.name || 'Untitled';

        // Moving to a folder
        const message =
          itemCount === 1
            ? `Moved 1 item to "${folderName}"`
            : `Moved ${itemCount} items to "${folderName}"`;
        toast.success(message);
      } else {
        // Moving to root (removing from folder)
        const message =
          itemCount === 1
            ? 'Moved 1 item to root'
            : `Moved ${itemCount} items to root`;
        toast.success(message);
      }

      // Perform optimistic update
      utils.workspace.all.setData(undefined, (old) => {
        if (!old) return old;

        return {
          ...old,
          entities: old.entities.map((entity) =>
            ids.includes(entity.id)
              ? {
                  ...entity,
                  groupId: folderId,
                  ...(teamId !== undefined && { teamId }),
                }
              : entity,
          ),
        };
      });

      // Return context to be used in case of error
      return { prevData };
    },
    // If the mutation fails, roll back and show error toast
    onError: (_err, { ids }, context) => {
      if (context?.prevData) {
        utils.workspace.all.setData(undefined, context.prevData);
      }

      const itemCount = ids.length;
      const message =
        itemCount === 1 ? 'Failed to move item' : 'Failed to move items';
      toast.error(message);
    },
  });

  return mutation;
};
